import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { exportToCsv } from "../../lib/exportCsv";
import AdminConfirmDialog from "./AdminConfirmDialog";
import BulkActionBar from "./BulkActionBar";
import ImageUploader from "../../components/ImageUploader";
import {
    Plus, Megaphone, Eye, MousePointerClick, Pause, Play, Archive, X,
    Trophy, ThumbsUp, ThumbsDown, BadgeCheck, Clock,
} from "lucide-react";

const PLACEMENTS = ["feed", "marketplace", "explore", "sidebar"];
const AUDIENCES = ["all", "free", "adventure", "premium"];
const STATUSES = ["draft", "pending_review", "active", "paused", "archived"];
const LEADERBOARD_METRICS = [
    { id: "ctr", label: "Top CTR" },
    { id: "impressions", label: "Most impressions" },
    { id: "clicks", label: "Most clicks" },
    { id: "lowest_reach", label: "Lowest reach" },
];

const EMPTY_AD = {
    name: "", advertiser: "RealWorld Outdoors", headline: "", body: "", cta_label: "Learn more",
    destination_url: "", image_url: "", placements: ["feed"], target_audiences: ["all"],
    status: "draft", start_date: "", end_date: "", brand_id: "",
};

export default function AdvertisingTab() {
    const [items, setItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [editor, setEditor] = useState(null); // { mode: 'create'|'edit', ad }
    const [pending, setPending] = useState(null); // { ad, action, payload? }
    const [leaderMetric, setLeaderMetric] = useState("ctr");
    const [leaderboard, setLeaderboard] = useState([]);
    const [selected, setSelected] = useState([]);
    const [bulkConfirm, setBulkConfirm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/ads", { params: { status: statusFilter } });
            setItems(data.items || []);
            setSelected([]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const loadLeaderboard = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/ads/leaderboard", {
                params: { metric: leaderMetric, limit: 5 },
            });
            setLeaderboard(data.items || []);
        } catch { setLeaderboard([]); }
    }, [leaderMetric]);

    useEffect(() => { loadLeaderboard(); }, [loadLeaderboard]);

    const onConfirm = async (reason) => {
        if (!pending) return;
        const { ad, action, payload } = pending;
        try {
            if (action === "delete") {
                await api.delete(`/admin/ads/${ad.id}`, { params: { reason } });
                toast.success("Campaign archived");
            } else if (action === "approve") {
                await api.post(`/admin/ads/${ad.id}/approve`);
                toast.success("Campaign approved — now live");
            } else if (action === "reject") {
                await api.post(`/admin/ads/${ad.id}/reject`, { reason });
                toast.success("Campaign rejected");
            } else {
                await api.patch(`/admin/ads/${ad.id}`, { ...payload, reason });
                toast.success("Updated");
            }
            setPending(null);
            load();
            loadLeaderboard();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onSave = async (form) => {
        try {
            if (editor.mode === "create") {
                await api.post("/admin/ads", form);
                toast.success("Campaign created");
            } else {
                await api.patch(`/admin/ads/${editor.ad.id}`, { ...form, reason: "Updated via editor" });
                toast.success("Campaign saved");
            }
            setEditor(null);
            load();
            loadLeaderboard();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const toggleAll = () =>
        setSelected(selected.length === items.length ? [] : items.map((a) => a.id));
    const toggleOne = (id) =>
        setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

    const runBulk = async (newStatus, reason) => {
        try {
            const { data } = await api.post("/admin/ads/bulk", {
                ids: selected,
                status: newStatus,
                reason,
            });
            toast.success(`${data.affected} campaign(s) updated`);
            setBulkConfirm(null);
            load();
            loadLeaderboard();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onExportCsv = () => {
        const rows = selected.length
            ? items.filter((a) => selected.includes(a.id))
            : items;
        exportToCsv(
            rows,
            [
                { key: "id", label: "ID" },
                { key: "name", label: "Name" },
                { key: "advertiser", label: "Advertiser" },
                { key: "headline", label: "Headline" },
                { key: "placement", label: "Placements", map: (r) => (r.placements?.length ? r.placements.join("|") : (r.placement || "")) },
                { key: "target_audience", label: "Audiences", map: (r) => (r.target_audiences?.length ? r.target_audiences.join("|") : (r.target_audience || "")) },
                { key: "status", label: "Status" },
                { key: "impressions", label: "Impressions" },
                { key: "clicks", label: "Clicks" },
                { key: "ctr", label: "CTR %", map: (r) => (r.impressions ? ((r.clicks / r.impressions) * 100).toFixed(2) : "0.00") },
                { key: "created_at", label: "Created" },
            ],
            "ad-campaigns"
        );
    };

    return (
        <div className="space-y-4">
            <Leaderboard
                items={leaderboard}
                metric={leaderMetric}
                onMetric={setLeaderMetric}
            />

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex gap-1 bg-snowcap border border-ridge-line rounded-lg p-1 flex-wrap">
                    {["all", ...STATUSES].map((s) => (
                        <button
                            key={s}
                            data-testid={`ads-filter-${s}`}
                            onClick={() => setStatusFilter(s)}
                            className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-widest transition-all whitespace-nowrap ${
                                statusFilter === s ? "bg-midnight-pine text-white" : "text-slate-gray hover:text-granite"
                            }`}
                        >
                            {s.replace("_", " ")}
                        </button>
                    ))}
                </div>
                <button
                    data-testid="ads-new-btn"
                    onClick={() => setEditor({ mode: "create", ad: EMPTY_AD })}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ember-orange text-white text-sm font-bold hover:bg-ember-dark active:scale-95 transition-all"
                >
                    <Plus className="w-4 h-4" /> New campaign
                </button>
            </div>

            {selected.length > 0 && (
                <BulkActionBar
                    totalVisible={items.length}
                    selectedIds={selected}
                    onToggleSelectAll={toggleAll}
                    onClear={() => setSelected([])}
                    onExportCsv={onExportCsv}
                    testidPrefix="admin-ads-bulk"
                    actions={[
                        { id: "activate", label: "Activate", onClick: () => setBulkConfirm({ status: "active", title: `Activate ${selected.length} campaign(s)?`, requireReason: false }) },
                        { id: "pause", label: "Pause", onClick: () => setBulkConfirm({ status: "paused", title: `Pause ${selected.length} campaign(s)?`, requireReason: false }) },
                        { id: "archive", label: "Archive", danger: true, onClick: () => setBulkConfirm({ status: "archived", title: `Archive ${selected.length} campaign(s)?`, requireReason: false }) },
                    ]}
                />
            )}

            {loading ? (
                <div className="space-y-3">
                    {[0, 1].map((i) => (
                        <div key={i} className="h-32 bg-snowcap border border-ridge-line rounded-lg animate-pulse" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="bg-snowcap border border-ridge-line rounded-lg p-12 text-center">
                    <Megaphone className="w-10 h-10 text-slate-gray mx-auto mb-3" />
                    <h3 className="font-display font-bold text-lg text-midnight-pine mb-1">No campaigns</h3>
                    <p className="text-slate-gray text-sm mb-4">Spin up your first ad to start filling Feed and Marketplace banners.</p>
                    <button
                        data-testid="ads-empty-cta"
                        onClick={() => setEditor({ mode: "create", ad: EMPTY_AD })}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-midnight-pine text-white text-sm font-bold hover:bg-deep-evergreen"
                    >
                        <Plus className="w-4 h-4" /> Create campaign
                    </button>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {items.map((ad) => (
                        <CampaignCard
                            key={ad.id}
                            ad={ad}
                            selected={selected.includes(ad.id)}
                            onToggleSelect={() => toggleOne(ad.id)}
                            onEdit={() => setEditor({ mode: "edit", ad })}
                            onPause={() => setPending({ ad, action: "patch", payload: { status: "paused" } })}
                            onActivate={() => setPending({ ad, action: "patch", payload: { status: "active" } })}
                            onArchive={() => setPending({ ad, action: "delete" })}
                            onApprove={() => setPending({ ad, action: "approve" })}
                            onReject={() => setPending({ ad, action: "reject" })}
                        />
                    ))}
                </div>
            )}

            {editor && (
                <CampaignEditor
                    mode={editor.mode}
                    initial={editor.ad}
                    onClose={() => setEditor(null)}
                    onSave={onSave}
                />
            )}

            <AdminConfirmDialog
                open={!!pending}
                onClose={() => setPending(null)}
                onConfirm={onConfirm}
                title={
                    pending?.action === "delete"
                        ? `Archive "${pending?.ad?.name}"?`
                        : pending?.action === "approve"
                        ? `Approve "${pending?.ad?.name}"?`
                        : pending?.action === "reject"
                        ? `Reject "${pending?.ad?.name}"?`
                        : pending?.payload?.status === "paused"
                        ? `Pause "${pending?.ad?.name}"?`
                        : pending?.payload?.status === "active"
                        ? `Activate "${pending?.ad?.name}"?`
                        : "Update campaign"
                }
                description={
                    pending?.action === "delete"
                        ? "The campaign will move to Archived. Performance history is preserved."
                        : pending?.action === "approve"
                        ? "Goes live immediately. Submitter will see it in their dashboard as Active."
                        : pending?.action === "reject"
                        ? "Submitter sees the rejection reason and can revise. Campaign moves to Draft."
                        : "Logged to the audit trail."
                }
                confirmLabel={
                    pending?.action === "delete" ? "Archive"
                    : pending?.action === "approve" ? "Approve & go live"
                    : pending?.action === "reject" ? "Reject"
                    : "Confirm"
                }
                danger={pending?.action === "delete" || pending?.action === "reject" || pending?.payload?.status === "paused"}
                requireReason={pending?.action !== "approve" && pending?.action !== "patch"}
            />

            <AdminConfirmDialog
                open={!!bulkConfirm}
                onClose={() => setBulkConfirm(null)}
                onConfirm={(reason) => runBulk(bulkConfirm.status, reason)}
                title={bulkConfirm?.title || ""}
                description="Applies to every selected campaign. Logged to the audit trail."
                confirmLabel="Apply"
                requireReason={bulkConfirm?.requireReason}
            />
        </div>
    );
}

function Leaderboard({ items, metric, onMetric }) {
    return (
        <section
            data-testid="ads-leaderboard"
            className="bg-snowcap border border-ridge-line rounded-lg p-5"
        >
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <h2 className="font-display font-bold text-lg text-midnight-pine flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-ember-orange" /> Performance leaderboard
                </h2>
                <div className="flex flex-wrap gap-1 bg-alpine-mist border border-ridge-line rounded-md p-1">
                    {LEADERBOARD_METRICS.map((m) => (
                        <button
                            key={m.id}
                            data-testid={`leaderboard-metric-${m.id}`}
                            onClick={() => onMetric(m.id)}
                            className={`px-3 py-1 rounded text-[11px] font-bold uppercase tracking-widest transition-all ${
                                metric === m.id ? "bg-midnight-pine text-white" : "text-slate-gray hover:text-granite"
                            }`}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            </div>
            {items.length === 0 ? (
                <p className="text-sm text-slate-gray italic">
                    No performance data yet. Active campaigns with at least one impression will appear here.
                </p>
            ) : (
                <ol className="space-y-2">
                    {items.map((row, i) => (
                        <li
                            key={row.id}
                            data-testid={`leaderboard-row-${i}`}
                            className="grid grid-cols-[auto,1fr,auto] items-center gap-3 py-1.5 px-3 rounded-md hover:bg-alpine-mist/50"
                        >
                            <span className="font-display font-black text-lg text-ember-orange w-6 text-center">
                                {i + 1}
                            </span>
                            <div className="min-w-0">
                                <div className="font-semibold text-granite truncate">{row.name}</div>
                                <div className="text-xs text-slate-gray truncate">
                                    {row.advertiser} · {row.placements?.length ? row.placements.join(", ") : (row.placement || "—")}
                                </div>
                            </div>
                            <div className="text-right text-xs font-bold whitespace-nowrap">
                                {metric === "ctr" && (
                                    <span className="text-ember-orange">{row.ctr}% CTR</span>
                                )}
                                {metric === "impressions" && (
                                    <span className="text-granite">{row.impressions.toLocaleString()} imp</span>
                                )}
                                {metric === "clicks" && (
                                    <span className="text-granite">{row.clicks.toLocaleString()} clicks</span>
                                )}
                                {metric === "lowest_reach" && (
                                    <span className="text-slate-gray">{row.impressions} imp · needs boost</span>
                                )}
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </section>
    );
}

function CampaignCard({ ad, selected, onToggleSelect, onEdit, onPause, onActivate, onArchive, onApprove, onReject }) {
    const ctr = ad.impressions ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : "0.00";
    const isPending = ad.status === "pending_review";
    const isPartner = ad.submitted_by_role && ad.submitted_by_role !== "admin";
    return (
        <div
            data-testid={`ad-card-${ad.id}`}
            className={`bg-snowcap border rounded-lg overflow-hidden flex flex-col relative ${
                isPending ? "border-ember-orange/50 ring-2 ring-ember-orange/20" : "border-ridge-line"
            }`}
        >
            <div className="absolute z-10 p-2">
                <input
                    type="checkbox"
                    data-testid={`ad-cb-${ad.id}`}
                    checked={!!selected}
                    onChange={onToggleSelect}
                    className="w-4 h-4 bg-white/90 rounded"
                />
            </div>
            {ad.image_url && (
                <img src={ad.image_url} alt="" className="w-full h-32 object-cover" />
            )}
            <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                        <h3 className="font-display font-bold text-base text-midnight-pine truncate">{ad.name}</h3>
                        <div className="text-xs text-slate-gray truncate flex items-center gap-1.5 flex-wrap">
                            {ad.advertiser}
                            {isPartner && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-trail-tan/50 text-[10px] font-bold uppercase tracking-widest text-granite">
                                    <BadgeCheck className="w-3 h-3" /> Partner
                                </span>
                            )}
                        </div>
                    </div>
                    <AdStatusPill status={ad.status} />
                </div>
                <div className="text-sm text-granite mb-2 line-clamp-2">{ad.headline}</div>
                {ad.rejection_reason && (
                    <div className="text-xs bg-ember-orange/10 border border-ember-orange/30 text-ember-dark rounded-md px-2 py-1 mb-2">
                        <span className="font-bold">Rejected:</span> {ad.rejection_reason}
                    </div>
                )}
                <div className="flex items-center gap-2 text-xs text-slate-gray mb-3 mt-auto flex-wrap">
                    {(ad.placements?.length ? ad.placements : (ad.placement ? [ad.placement] : [])).map((p) => (
                        <span key={p} className="capitalize bg-alpine-mist px-2 py-0.5 rounded-full font-semibold">{p}</span>
                    ))}
                    <span className="capitalize">
                        {(ad.target_audiences?.length ? ad.target_audiences : (ad.target_audience ? [ad.target_audience] : [])).join(", ")} users
                    </span>
                    {ad.submitted_by_name && (
                        <span className="text-[11px]">by {ad.submitted_by_name}</span>
                    )}
                </div>
                <div className="flex items-center gap-4 text-xs font-semibold mb-3">
                    <span className="flex items-center gap-1 text-slate-gray">
                        <Eye className="w-3 h-3" /> {ad.impressions || 0}
                    </span>
                    <span className="flex items-center gap-1 text-slate-gray">
                        <MousePointerClick className="w-3 h-3" /> {ad.clicks || 0}
                    </span>
                    <span className="text-ember-orange font-bold">{ctr}% CTR</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {isPending ? (
                        <>
                            <button
                                data-testid={`ad-approve-${ad.id}`}
                                onClick={onApprove}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-md bg-deep-evergreen text-white hover:bg-deep-evergreen/90"
                            >
                                <ThumbsUp className="w-3 h-3" /> Approve
                            </button>
                            <button
                                data-testid={`ad-reject-${ad.id}`}
                                onClick={onReject}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-md bg-ember-orange/10 text-ember-dark hover:bg-ember-orange hover:text-white"
                            >
                                <ThumbsDown className="w-3 h-3" /> Reject
                            </button>
                            <button
                                data-testid={`ad-edit-${ad.id}`}
                                onClick={onEdit}
                                className="px-2.5 py-1 text-xs font-bold rounded-md border border-ridge-line hover:border-midnight-pine"
                            >
                                Edit
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                data-testid={`ad-edit-${ad.id}`}
                                onClick={onEdit}
                                className="px-2.5 py-1 text-xs font-bold rounded-md border border-ridge-line hover:border-midnight-pine"
                            >
                                Edit
                            </button>
                            {ad.status === "active" ? (
                                <button
                                    data-testid={`ad-pause-${ad.id}`}
                                    onClick={onPause}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md bg-trail-tan/40 text-granite hover:bg-trail-tan"
                                >
                                    <Pause className="w-3 h-3" /> Pause
                                </button>
                            ) : ad.status === "paused" || ad.status === "draft" ? (
                                <button
                                    data-testid={`ad-activate-${ad.id}`}
                                    onClick={onActivate}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md bg-deep-evergreen/15 text-deep-evergreen hover:bg-deep-evergreen hover:text-white"
                                >
                                    <Play className="w-3 h-3" /> Activate
                                </button>
                            ) : null}
                            {ad.status !== "archived" && (
                                <button
                                    data-testid={`ad-archive-${ad.id}`}
                                    onClick={onArchive}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md text-slate-gray hover:text-ember-dark hover:bg-ember-orange/10"
                                >
                                    <Archive className="w-3 h-3" /> Archive
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function AdStatusPill({ status }) {
    const styles = {
        active: "bg-deep-evergreen/15 text-deep-evergreen",
        paused: "bg-trail-tan/40 text-granite",
        draft: "bg-alpine-mist text-slate-gray",
        archived: "bg-ridge-line text-slate-gray",
        pending_review: "bg-ember-orange/15 text-ember-dark",
    };
    return (
        <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest shrink-0 whitespace-nowrap ${
                styles[status] || styles.draft
            }`}
        >
            {(status || "draft").replace("_", " ")}
        </span>
    );
}

function CampaignEditor({ mode, initial, onClose, onSave }) {
    const [form, setForm] = useState(() => {
        const init = { ...EMPTY_AD, ...initial };
        // Back-compat: derive multi-select arrays from legacy single fields.
        if (!Array.isArray(init.placements) || init.placements.length === 0) {
            init.placements = initial?.placement ? [initial.placement] : ["feed"];
        }
        if (!Array.isArray(init.target_audiences) || init.target_audiences.length === 0) {
            init.target_audiences = initial?.target_audience ? [initial.target_audience] : ["all"];
        }
        return init;
    });
    const [brands, setBrands] = useState([]);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleIn = (k, val) => setForm((f) => {
        const cur = Array.isArray(f[k]) ? f[k] : [];
        return { ...f, [k]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
    });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/brands/options");
                setBrands(data.items || []);
            } catch {
                setBrands([]);
            }
        })();
    }, []);

    const submit = () => {
        if (!form.name?.trim() || !form.headline?.trim() || !form.destination_url?.trim()) {
            toast.error("Name, headline and destination URL are required.");
            return;
        }
        if (!form.placements?.length) {
            toast.error("Pick at least one placement.");
            return;
        }
        if (!form.target_audiences?.length) {
            toast.error("Pick at least one audience.");
            return;
        }
        onSave(form);
    };

    return (
        <div
            data-testid="ad-editor"
            className="fixed inset-0 z-50 flex items-center justify-center bg-midnight-pine/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="bg-snowcap rounded-lg shadow-2xl border border-ridge-line w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-ridge-line sticky top-0 bg-snowcap z-10">
                    <h3 className="font-display font-bold text-xl text-midnight-pine">
                        {mode === "create" ? "New campaign" : "Edit campaign"}
                    </h3>
                    <button onClick={onClose} className="text-slate-gray hover:text-granite p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-5 grid md:grid-cols-2 gap-4">
                    <Field label="Campaign name" required>
                        <input
                            data-testid="ad-form-name"
                            value={form.name} onChange={(e) => set("name", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Advertiser">
                        <input
                            data-testid="ad-form-advertiser"
                            value={form.advertiser} onChange={(e) => set("advertiser", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Brand (links campaign to /brands)" full>
                        <select
                            data-testid="ad-form-brand"
                            value={form.brand_id || ""}
                            onChange={(e) => set("brand_id", e.target.value)}
                            className={inputCls}
                        >
                            <option value="">— No brand (unlinked) —</option>
                            {brands.map((b) => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                        <p className="text-xs text-slate-gray mt-1">
                            Pick a brand from <span className="font-semibold">Admin → Brands</span> to make this campaign show on the public <span className="font-semibold">/brands</span> page (and Brand-of-the-week).
                        </p>
                    </Field>
                    <Field label="Headline" required>
                        <input
                            data-testid="ad-form-headline"
                            value={form.headline} onChange={(e) => set("headline", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="CTA label">
                        <input
                            data-testid="ad-form-cta"
                            value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Body copy" full>
                        <textarea
                            data-testid="ad-form-body"
                            value={form.body} onChange={(e) => set("body", e.target.value)}
                            rows={2}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Destination URL" full required>
                        <input
                            data-testid="ad-form-destination"
                            value={form.destination_url} onChange={(e) => set("destination_url", e.target.value)}
                            placeholder="https://…"
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Image (optional)" full>
                        <ImageUploader
                            value={form.image_url}
                            onChange={(url) => set("image_url", url)}
                            aspect="aspect-square"
                            testid="ad-form-image"
                            allowPaste
                            fallback={<span className="text-xs text-slate-gray">Ad</span>}
                        />
                    </Field>
                    <Field label="Placements" full>
                        <div data-testid="ad-form-placements" className="flex flex-wrap gap-2">
                            {PLACEMENTS.map((p) => {
                                const on = form.placements?.includes(p);
                                return (
                                    <button
                                        key={p}
                                        type="button"
                                        data-testid={`ad-form-placement-${p}`}
                                        aria-pressed={on}
                                        onClick={() => toggleIn("placements", p)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider capitalize transition-all ${
                                            on ? "bg-midnight-pine text-white" : "bg-alpine-mist text-granite border border-ridge-line hover:border-midnight-pine"
                                        }`}
                                    >
                                        {p}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-xs text-slate-gray mt-1.5">Where this ad can appear. Pick one or more surfaces.</p>
                    </Field>
                    <Field label="Audiences" full>
                        <div data-testid="ad-form-audiences" className="flex flex-wrap gap-2">
                            {AUDIENCES.map((a) => {
                                const on = form.target_audiences?.includes(a);
                                return (
                                    <button
                                        key={a}
                                        type="button"
                                        data-testid={`ad-form-audience-${a}`}
                                        aria-pressed={on}
                                        onClick={() => toggleIn("target_audiences", a)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider capitalize transition-all ${
                                            on ? "bg-ember-orange text-white" : "bg-alpine-mist text-granite border border-ridge-line hover:border-ember-orange"
                                        }`}
                                    >
                                        {a}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-xs text-slate-gray mt-1.5">
                            Who sees it by tier. <span className="font-semibold">all</span> targets everyone (premium users never see ads).
                        </p>
                    </Field>
                    <Field label="Start date">
                        <input
                            data-testid="ad-form-start"
                            type="date"
                            value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="End date">
                        <input
                            data-testid="ad-form-end"
                            type="date"
                            value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Status">
                        <select
                            data-testid="ad-form-status"
                            value={form.status} onChange={(e) => set("status", e.target.value)}
                            className={inputCls}
                        >
                            {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                        </select>
                    </Field>

                    <div className="md:col-span-2">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-2">Preview</div>
                        <PreviewCard form={form} />
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 p-5 border-t border-ridge-line bg-alpine-mist/30 sticky bottom-0">
                    <button onClick={onClose} className="px-4 py-2 rounded-md text-sm font-bold text-granite hover:bg-alpine-mist">
                        Cancel
                    </button>
                    <button
                        data-testid="ad-form-save"
                        onClick={submit}
                        className="px-4 py-2 rounded-md text-sm font-bold bg-midnight-pine text-white hover:bg-deep-evergreen"
                    >
                        {mode === "create" ? "Create" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const inputCls =
    "w-full px-3 py-2 rounded-md bg-alpine-mist border border-ridge-line text-sm focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 capitalize-none";

function Field({ label, children, required, full }) {
    return (
        <label className={`block ${full ? "md:col-span-2" : ""}`}>
            <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1.5">
                {label}{required && <span className="text-ember-orange ml-1">*</span>}
            </span>
            {children}
        </label>
    );
}

function PreviewCard({ form }) {
    return (
        <div data-testid="ad-form-preview" className="bg-trail-tan/40 border border-dashed border-ridge-line rounded-lg p-5 flex items-center gap-4">
            {form.image_url ? (
                <img src={form.image_url} alt="" className="w-20 h-20 rounded-md object-cover shrink-0" />
            ) : (
                <div className="w-20 h-20 bg-alpine-mist rounded-md border border-ridge-line shrink-0 flex items-center justify-center text-[10px] text-slate-gray">
                    Ad
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1">
                    Sponsored · {form.advertiser || "RealWorld Outdoors"}
                </div>
                <div className="font-display font-bold text-midnight-pine text-base leading-tight truncate">
                    {form.headline || "Headline goes here"}
                </div>
                <div className="text-xs text-slate-gray mt-1 line-clamp-2">
                    {form.body || "Body copy preview"}
                </div>
                <button className="mt-2 px-3 py-1 rounded-md bg-midnight-pine text-white text-xs font-bold">
                    {form.cta_label || "Learn more"}
                </button>
            </div>
        </div>
    );
}
